'use client';

import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverFacetsQuery } from '@loci/api';

import { CATEGORY_ORDER } from '@/config/categories';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import {
  formatDiscoverScopeCaptionPhrase,
  resolveDiscoverScopeKey,
} from '@/lib/discover/resultsCountCopy';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';

import { DiscoverScopeCaptionView } from './DiscoverScopeCaptionView';

export interface DiscoverScopeCaptionProps {
  /** Active city — threaded into the shared query args (WEB-263). */
  readonly cityCode: CityCode;
  /** Wrapper class names forwarded to the underlying view. */
  readonly className?: string;
}

/**
 * Connected `DiscoverScopeCaption` (WEB-659 / DPL-09) — the desktop-rail
 * scope caption anchoring the Category facet counts to a stated scope:
 * `"10 categories · 47 events tonight citywide"`.
 *
 * **Single-fetch invariant:** builds its args via the SAME
 * `useDiscoverEventArgs` hook every other Discover subscriber
 * (`DiscoverCategoryFilter`, `DiscoverNeighborhoodFilter`, the list view)
 * uses, so RTK Query coalesces this subscription onto the shared
 * `/events/discover` request instead of firing a new one — no separate
 * `limit=0` probe is reintroduced (removed in API-1416).
 *
 * `eventCount` reads `meta.total` from that same response — the total
 * matching every currently active filter, so the caption's number always
 * matches what the rest of the rail/list is showing. `categoryCount` is the
 * static canonical-ten length (`CATEGORY_ORDER.length`), since all ten
 * always render regardless of count (WEB-606).
 *
 * `MobileFilterDrawer` does NOT mount this connected component — it already
 * fetches the same facets response via its own `useDiscoverFacetsQuery`
 * call (a structurally different args shape than `useDiscoverEventArgs`) and
 * would otherwise be pointing this component at a second, uncoalesced
 * request. It renders `DiscoverScopeCaptionView` directly against its
 * already-fetched `currentData` instead.
 */
export function DiscoverScopeCaption({
  cityCode,
  className,
}: DiscoverScopeCaptionProps): React.ReactElement {
  const { params } = useDiscoverParams();
  const queryArgs = useDiscoverEventArgs(params, cityCode);
  const { currentData } = useDiscoverFacetsQuery(queryArgs);
  const dateLabel = formatDiscoverScopeCaptionPhrase(
    resolveDiscoverScopeKey(currentData?.facets?.scopeKey, params.date),
  );

  return (
    <DiscoverScopeCaptionView
      categoryCount={CATEGORY_ORDER.length}
      eventCount={currentData?.meta?.total ?? null}
      dateLabel={dateLabel}
      className={className}
    />
  );
}
